import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import { MdMovie, MdTv, MdAnimation, MdPeople, MdSearch, MdPerson } from "react-icons/md";
import api from "services/api";

const R2 = process.env.REACT_APP_R2_PUBLIC_URL || "https://pub-26704679c67f47a28bce53f5dac3f586.r2.dev";

const TYPE_META = {
  MOVIE:     { label: "Movies",    icon: MdMovie,     path: "movies" },
  SERIES:    { label: "Series",    icon: MdTv,        path: "series" },
  ANIMATION: { label: "Animation", icon: MdAnimation, path: "animation" },
};

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = (searchParams.get("q") || "").trim();

  const [titles, setTitles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getTitles().catch(() => []),
      api.getUsers().catch(() => []),
    ]).then(([t, u]) => {
      if (cancelled) return;
      setTitles(Array.isArray(t) ? t : t?.titles || []);
      setUsers(Array.isArray(u) ? u : u?.users || []);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const term = q.toLowerCase();
  const matchedTitles = term ? titles.filter(t => t.title.toLowerCase().includes(term)) : [];
  const matchedUsers = term ? users.filter(u =>
    (u.name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term)
  ) : [];

  const byType = { MOVIE: [], SERIES: [], ANIMATION: [] };
  for (const t of matchedTitles) (byType[t.type] || (byType[t.type] = [])).push(t);

  const totalResults = matchedTitles.length + matchedUsers.length;

  return (
    <div>
      <div className="mt-3 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
          Search results for "{q}"
        </h1>
      </div>

      {loading ? (
        <div className="mt-10 flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : !q ? (
        <Card extra="mt-5 w-full px-6 py-10 flex flex-col items-center">
          <MdSearch className="mb-3 h-12 w-12 text-gray-200" />
          <p className="text-lg font-bold text-navy-700 dark:text-white">Type something to search</p>
        </Card>
      ) : totalResults === 0 ? (
        <Card extra="mt-5 w-full px-6 py-10 flex flex-col items-center">
          <MdSearch className="mb-3 h-12 w-12 text-gray-200" />
          <p className="text-lg font-bold text-navy-700 dark:text-white">No results</p>
          <p className="mt-1 text-sm text-gray-400">Try a different search</p>
        </Card>
      ) : (
        <div className="mt-5 flex flex-col gap-8">
          {Object.entries(TYPE_META).map(([type, meta]) => {
            const rows = byType[type];
            if (!rows || rows.length === 0) return null;
            const Icon = meta.icon;
            return (
              <div key={type}>
                <div className="mb-4 flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
                  <Icon className="h-5 w-5 text-brand-500" /> {meta.label}
                  <span className="text-sm font-medium text-gray-400">({rows.length})</span>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5">
                  {rows.map(item => (
                    <Card
                      key={item.id}
                      extra="group flex flex-col w-full h-full !p-4 3xl:p-![18px] bg-white dark:bg-navy-800 transition-all hover:-translate-y-1 hover:shadow-xl hover:cursor-pointer border border-transparent hover:border-brand-500/20"
                      onClick={() => navigate(`/admin/${meta.path}/${item.id}`)}
                    >
                      <div className="h-full w-full">
                        {item.posterKey && R2 ? (
                          <img
                            src={`${R2}/${item.posterKey}`}
                            className="mb-3 h-full w-full rounded-xl object-cover aspect-[2/3] 3xl:h-full 3xl:w-full transition-transform group-hover:scale-[1.02]"
                            alt={item.title}
                          />
                        ) : (
                          <div className="mb-3 flex h-full w-full items-center justify-center rounded-xl bg-lightPrimary dark:bg-navy-700 aspect-[2/3] transition-transform group-hover:scale-[1.02]">
                            <Icon className="h-16 w-16 text-gray-400" />
                          </div>
                        )}

                        <div className="mb-1 px-1">
                          <p className="text-lg font-bold text-navy-700 dark:text-white">{item.title}</p>
                          <p className="mt-1 text-sm font-medium text-gray-600">
                            {item.vj || "Unknown"} · {item.year}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}

          {matchedUsers.length > 0 && (
            <div>
              <div className="mb-4 flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
                <MdPeople className="h-5 w-5 text-brand-500" /> Users
                <span className="text-sm font-medium text-gray-400">({matchedUsers.length})</span>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5">
                {matchedUsers.map(u => (
                  <Card
                    key={u.id}
                    extra="group flex flex-col items-center w-full !p-6 bg-white dark:bg-navy-800 transition-all hover:-translate-y-1 hover:shadow-xl hover:cursor-pointer border border-transparent hover:border-brand-500/20"
                    onClick={() => navigate(`/admin/users`)}
                  >
                    {u.avatarUrl && R2 ? (
                      <img
                        src={`${R2}/${u.avatarUrl}`}
                        className="mb-3 h-20 w-20 rounded-full object-cover"
                        alt={u.name || u.email}
                      />
                    ) : (
                      <div className="mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-lightPrimary dark:bg-navy-700">
                        <MdPerson className="h-10 w-10 text-gray-400" />
                      </div>
                    )}
                    <p className="text-base font-bold text-navy-700 dark:text-white">{u.name || "Unnamed"}</p>
                    <p className="mt-1 text-sm text-gray-500">{u.email}</p>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
